import { useCallback } from 'react';
import { detectContentType, CONTENT_TYPES } from '../utils/normalizeCardData';

/**
 * Build the schedule payload from a raw item.
 * Extracts and normalizes the fields ScheduleActivityModal expects.
 *
 * @param {Object} item - Raw activity/event item
 * @returns {Object} Payload for setActivityToSchedule
 */
export const buildSchedulePayload = (item) => {
  const type = detectContentType(item);
  const isFixedTimeEvent = type === CONTENT_TYPES.ARTIST_EVENT || type === CONTENT_TYPES.EVENT;
  // A feed place card carries its place id only in `id` (C368(6)). Only a
  // card typed 'place' lends its id as a place id — an event's id never
  // becomes one (C363(11)).
  const isPlaceCard = item.content_type === 'place';
  const venue = item.venue || item.venue_name;

  return {
    id: item.id,
    title: item.title,
    // A ranked What's On event row has a venue and a city but no `location`
    // (the follow bands' rows are given one by eventCardShape); the schedule
    // form prefills from this (C368(9)).
    location: item.location
      || (isFixedTimeEvent ? ([venue, item.city].filter(Boolean).join(', ') || undefined) : undefined),
    category: item.category,
    imageUrl: item.cover_photo,
    eventDate: isFixedTimeEvent ? (item.event_date || item.eventDate) : undefined,
    eventTime: isFixedTimeEvent ? (item.event_time || item.eventTime) : undefined,
    placeId: item.place_id || item.placeId || (isPlaceCard ? item.id : undefined),
    venue,
    source: item.source,
    content_type: type
  };
};

/**
 * useDiscoverCardActions - Builds the actions object for UnifiedCard in DISCOVER context.
 *
 * Maps each content type to the appropriate handler from the modals object
 * returned by useFeedModals. Eliminates the 9+ inline action-wiring blocks
 * that were duplicated across SocialFeed.js rendering paths.
 *
 * @param {Object} modals - The modals object from useFeedModals
 * @returns {Function} buildActions(item) - Returns actions object for the given item
 */
const useDiscoverCardActions = (modals) => {
  const buildActions = useCallback((item) => {
    if (!item || !modals) return {};

    const type = detectContentType(item);

    // Overflow menu actions — only shown on non-owned content
    const overflowActions = !item.is_own_content ? {
      onNotInterested: () => modals.handleNotInterested(item),
      onReport: () => modals.handleReport(item),
      onBlockUser: (item.user_id || item.creator_id) ? () => modals.handleBlockUser(item) : undefined
    } : {};

    switch (type) {
      case CONTENT_TYPES.ACTIVITY:
        return {
          // A PLACE card falls into this case (CONTENT_TYPES has no 'place', so
          // detectContentType returns ACTIVITY), but its like must go to the
          // server as 'place'. The server shows a place card's hearts across
          // every id the place is known under, and only a toggle entered as
          // 'place' removes across those same ids. Sent as 'activity', a heart
          // taken on the place's own page showed on the card and every tap
          // flipped a different, hidden row — it could never be cleared from
          // the feed. Comments and shares stay 'activity': commentsService does
          // not accept 'place' at all.
          onLike: () => modals.handleLike({
            ...item,
            content_type: item.content_type === 'place' ? 'place' : 'activity'
          }),
          onComment: () => modals.handleOpenComments({ ...item, content_type: 'activity' }),
          onSave: () => modals.handleSaveActivity(item),
          onSchedule: () => modals.handleScheduleActivity(buildSchedulePayload(item)),
          onShare: item.is_own_content ? () => modals.handleShare({ ...item, content_type: 'activity' }) : undefined,
          ...overflowActions
        };

      case CONTENT_TYPES.ARTIST_EVENT:
        return {
          onLike: () => modals.handleLike(item),
          onComment: () => modals.handleOpenComments({ ...item, content_type: 'artist_event' }),
          onSave: () => modals.handleSaveActivity(item),
          onSchedule: () => modals.handleScheduleActivity(buildSchedulePayload(item)),
          ...overflowActions
        };

      case CONTENT_TYPES.EVENT:
        return {
          onLike: () => modals.handleLike(item),
          onComment: () => modals.handleOpenComments({ ...item, content_type: 'event' }),
          onSave: () => modals.handleSaveActivity(item),
          onSchedule: () => modals.handleScheduleActivity(buildSchedulePayload(item)),
          ...overflowActions
        };

      case CONTENT_TYPES.EVENT_GROUP:
        return {
          onLike: () => modals.handleLike({ ...item, content_type: 'event_group' }),
          onComment: () => modals.handleOpenComments({ ...item, content_type: 'event_group' }),
          onSave: () => modals.handleSave({ ...item, content_type: 'event_group' }),
          ...overflowActions
        };

      case CONTENT_TYPES.COLLECTION:
        return {
          onLike: () => modals.handleLike(item),
          onComment: () => modals.handleOpenComments(item),
          onSave: () => modals.handleSave(item),
          onShare: item.is_own_content ? () => modals.handleShare(item) : undefined,
          ...overflowActions
        };

      case CONTENT_TYPES.BOARD:
        return {
          onLike: () => modals.handleLike(item),
          onComment: () => modals.handleOpenComments(item),
          onSave: () => modals.handleSave(item),
          onShare: item.is_own_content ? () => modals.handleShare(item) : undefined,
          ...overflowActions
        };

      default:
        return {
          onLike: () => modals.handleLike(item),
          onComment: () => modals.handleOpenComments(item),
          onSave: () => modals.handleSave(item),
          onShare: item.is_own_content ? () => modals.handleShare(item) : undefined,
          ...overflowActions
        };
    }
  }, [modals]);

  return buildActions;
};

export default useDiscoverCardActions;
